// Shared criteria option definitions (stored value + i18n label key) for the
// could-buy form. Used by the MainStep controls, the SuccessStep summary and the
// MyAreasView read-back so the value→label mapping lives in exactly one place.

export interface CriteriaOption {
  value: string;
  key: string;
}

// Floor tabs (Figma 3612:75157); '' is the implicit "Any level" tab.
export const FLOOR_OPTIONS: CriteriaOption[] = [
  { value: 'highest', key: 'floorHighest' },
  { value: 'mid', key: 'floorMid' },
  { value: 'lowest', key: 'floorLowest' },
];

// Renovation status tabs (Figma 3611:94479).
export const RENOVATION_OPTIONS: CriteriaOption[] = [
  { value: 'recently_renovated', key: 'renovationRecent' },
  // 'good_condition', not 'good': this value now mirrors into
  // property_renovation_status, where every other producer and the card's
  // label lookup already use the longer form.
  { value: 'good_condition', key: 'renovationGood' },
  { value: 'needs_renovation', key: 'renovationNeeds' },
];

// Amenity checkboxes (Figma 3612:78413).
export const AMENITY_OPTIONS: CriteriaOption[] = [
  { value: 'elevator', key: 'amenityElevator' },
  { value: 'balcony', key: 'amenityBalcony' },
];

// Resolve a stored value to its translated label, or undefined when unset/unknown.
/**
 * Map a stored criteria value onto the option vocabulary. Only one value ever
 * diverged: the could-buy form used to write a bare 'good' where every other
 * producer writes 'good_condition', so interests saved before that carry it and
 * would otherwise match no option at all — losing the chip and leaving the edit
 * form's tab unselected.
 */
export const normalizeCriteriaValue = (value: string | undefined): string | undefined =>
  value === 'good' ? 'good_condition' : value;

export const criteriaOptionLabel = (
  options: CriteriaOption[],
  value: string | undefined,
  t: (key: string) => string,
): string | undefined => {
  const option = options.find((o) => o.value === normalizeCriteriaValue(value));
  return option ? t(`newLandingPage.couldBuy.main.${option.key}`) : undefined;
};

/**
 * The bare number behind a stored rooms-minimum. Every rooms option is a
 * minimum, so labels append their own "+" — but the top option is stored as
 * "5+" already, which rendered it as "5++". Strip the stored "+" here so the
 * quirk is handled in one place rather than at each label site.
 */
export const roomsMinValue = (roomsMin: string): string =>
  roomsMin.replace(/\+$/, '');
